import { Message } from "./ao"
import { dryrun } from "./ao-connection"
import { isAddress } from "../utils/utils"
import { TOKEN_BALANCE_TIMEOUT, TOKEN_INFO_TIMEOUT, TOKEN_MIRRORS } from "@/settings"
import { flattenTags, getTagValue } from "@/utils/arweave"
import { getHBBaseUrl } from "@/utils/hb-router"

export type PoolParams = {
  reservesThis: string
  reservesQuote: string
  totalFee: number
}
export type TokenInfo = {
  id: string
  denomination: number
  ticker: string
  logo: string
  name: string
  poolParams?: PoolParams
}

export type TokenHolder = {
  rank: number
  entityId: string
  balance: number
}

export async function getBalance(
  tokenId: string,
  address: string,
  timeout = TOKEN_BALANCE_TIMEOUT,
) {
  const res = await Promise.race<DryRunResult>([
    dryrun({
      Owner: address,
      process: TOKEN_MIRRORS[tokenId] || tokenId,
      tags: [
        { name: "Action", value: "Balance" },
        { name: "Recipient", value: address },
      ],
    }),
    new Promise((_, reject) =>
      setTimeout(() => reject(`Timeout reading Balance (${tokenId})`), timeout),
    ),
  ])

  if ("Messages" in res === false) {
    throw new Error(`Unknown response ${JSON.stringify(res)}`)
  }

  try {
    for (const msg of res.Messages as Message[]) {
      const balance = getTagValue("Balance", msg.Tags) || JSON.parse(msg.Data)

      return BigInt(balance)
    }

    throw new Error(`Balance response malformed ${tokenId}`)
  } catch (err) {
    console.error(`Failed to read balance ${tokenId}`, err)
    throw err
  }
}

export async function getNativeArBalance(address: string) {
  const res = await fetch(`https://arweave.net/wallet/${address}/balance`)
  const balance = await res.text()

  return BigInt(balance)
}

type BalanceMap = {
  [key: string]: string | number
}

export async function getTokenHolders(tokenInfo: TokenInfo): Promise<TokenHolder[]> {
  const result = await dryrun({
    process: tokenInfo.id,
    data: "",
    tags: [{ name: "Action", value: "Balances" }],
  })

  try {
    if (result.Messages.length === 0)
      throw new Error(`No response from (get) Balances (${tokenInfo.name})`)
    const balanceMap = JSON.parse(result.Messages[0].Data) as BalanceMap
    const tokenHolders = Object.keys(balanceMap)
      .filter((entityId) => balanceMap[entityId] !== "0" && balanceMap[entityId] !== 0)
      .sort((a, b) => Number(balanceMap[b]) - Number(balanceMap[a]))
      .map((entityId, index) => ({
        rank: index + 1,
        entityId,
        balance: Number(balanceMap[entityId]) / 10 ** tokenInfo.denomination,
      }))

    return tokenHolders
  } catch (err) {
    console.error(err)
  }

  return []
}

type Tag = {
  name: string
  value: string
}

type DryRunResult = Awaited<ReturnType<typeof dryrun>>

/**
 * Get info about a token
 * @param tokenId address of the token process
 */
export async function getTokenInfo(
  tokenId: string,
  timeout = TOKEN_INFO_TIMEOUT,
): Promise<TokenInfo> {
  if (!isAddress(tokenId)) {
    throw new Error(`Invalid tokenId ${tokenId}`)
  }

  const result = await Promise.race<DryRunResult>([
    dryrun({
      process: tokenId,
      tags: [{ name: "Action", value: "Info" }],
    }),
    new Promise((_, reject) =>
      setTimeout(() => reject(`Timeout reading Info (${tokenId})`), timeout),
    ),
  ])

  if (result.Messages.length === 0) throw new Error(`No response from (get) Info (${tokenId})`)
  const tagMap = flattenTags(result.Messages[0].Tags)

  const denomination = parseInt(tagMap["Denomination"])
  if (isNaN(denomination)) throw new Error(`Denomination is not a number ${tokenId}`)
  if (!tagMap["Name"]) throw new Error(`Token name is missing ${tokenId}`)
  if (!tagMap["Ticker"]) throw new Error(`Token ticker is missing ${tokenId}`)

  return {
    id: tokenId,
    name: tagMap["Name"],
    ticker: tagMap["Ticker"],
    denomination: denomination,
    logo: tagMap["Logo"],
  }
}

/**
 * Only works for LPs
 */

export async function getTotalSupplyHB(tokenId: string): Promise<string> {
  console.log("GET TOTAL SUPPLY HB", tokenId)
  const HB_API = getHBBaseUrl(tokenId)

  const response = await fetch(`${HB_API}/${tokenId}~process@1.0/compute/token-info/supply`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  })

  if (!response.ok) {
    throw new Error("Failed to fetch total supply")
  }

  const data = await response.text()

  return data
}

export async function getTotalSupplyDryRun(tokenId: string): Promise<string> {
  console.log("GET TOTAL SUPPLY", tokenId)
  const result = await dryrun({
    process: tokenId,
    tags: [{ name: "Action", value: "Total-Supply" }],
  })

  if (result.Messages.length === 0)
    throw new Error(`No response from (get) Total-Supply (${tokenId})`)
  const message = result.Messages[0]

  const tags = message.Tags as Tag[]
  const tag = tags.find((x) => x.name === "Total-Supply")
  const valueAsString = tag?.value || message.Data

  /*   let value = parseFloat(valueAsString ? valueAsString : "")

  if (isNaN(value)) throw new Error("Response malformed")
 */

  return valueAsString
}

export async function getTotalSupply(tokenId: string): Promise<string> {
  try {
    const response = await getTotalSupplyHB(tokenId)

    return response
  } catch (error) {
    console.error("Error fetching total supply from HB:", error)

    // Fallback to dryrun if HB fails
    try {
      const response = await getTotalSupplyDryRun(tokenId)
      return response
    } catch (dryRunError) {
      console.error("Error fetching total supply from dryrun:", dryRunError)
      throw dryRunError
    }
  }
}

export async function calcTotalSupply(tokenId: string): Promise<number> {
  const result = await dryrun({
    process: tokenId,
    tags: [{ name: "Action", value: "Balances" }],
  })

  try {
    if (result.Messages.length === 0)
      throw new Error(`No response from (get) Balances (${tokenId})`)
    const balanceMap = JSON.parse(result.Messages[0].Data) as BalanceMap
    const totalSupply = Object.values(balanceMap).reduce((acc: number, balance) => {
      return balance ? acc + Number(balance) : acc
    }, 0)

    return totalSupply
  } catch (err) {
    console.error(err)
  }

  return 0
}

export async function getTotalSupplyToken(tokenId: string): Promise<number> {
  const result = await dryrun({
    process: tokenId,
    tags: [{ name: "Action", value: "Total-Supply" }],
  })

  try {
    if (result.Messages.length === 0) return calcTotalSupply(tokenId)

    const message = result.Messages[0]
    const parsed = JSON.parse(message.Data)

    const data = parsed

    if (typeof data === "string") {
      return Number.parseInt(data)
    }

    if (typeof data === "number") {
      return data
    }
    return 0
  } catch (err) {
    console.error(err)
  }

  return 0
}
